import { toError } from "@clipboard-health/util-ts";
import { setAuthHeaderProvider } from "@src/appV2/Auth/cognito/authHeaderProviderRef";
import { getCognitoIdTokenWithProactiveRefresh } from "@src/appV2/Auth/cognito/getCognitoIdTokenWithProactiveRefresh";
import { logError } from "@src/appV2/lib/analytics";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib/analytics/events";
import constate from "constate";
import { useCallback, useEffect, useRef } from "react";

const BEARER_TOKEN_PREFIX = "Bearer ";
const MAX_TOKEN_ERROR_LOGS = 5;

/**
 * Hook that provides centralized authentication header values for API calls.
 * Always uses Cognito as the sole authentication provider.
 *
 * Returns values ready for the Authorization header: `Bearer <token>`
 *
 * Implements rate-limited logging to prevent log flooding.
 *
 * @returns An object with getAuthHeaderValue function that returns the formatted auth value
 */
function useAuthHeaderProvider() {
  const loggedErrorCount = useRef(0);

  const getAuthHeaderValue = useCallback(
    async (forceRefresh?: boolean): Promise<string | undefined> => {
      try {
        const token = await getCognitoIdTokenWithProactiveRefresh(forceRefresh);
        if (token) {
          return `${BEARER_TOKEN_PREFIX}${token}`;
        }

        return undefined;
      } catch (maybeError: unknown) {
        if (loggedErrorCount.current < MAX_TOKEN_ERROR_LOGS) {
          logError(APP_V2_APP_EVENTS.FAILED_TO_GET_COGNITO_TOKEN, {
            error: toError(maybeError),
          });
          loggedErrorCount.current += 1;
        }

        throw new Error("Unable to get user token");
      }
    },
    []
  );

  useEffect(() => {
    setAuthHeaderProvider(getAuthHeaderValue);
    return () => {
      setAuthHeaderProvider(undefined);
    };
  }, [getAuthHeaderValue]);

  return { getAuthHeaderValue };
}

export const [AuthHeaderProvider, useAuthHeaderContext] = constate(useAuthHeaderProvider);
